/**
 * Where Nucleus Plan Review opens on this app (app-shell `nucleus.planReviewPath`).
 *
 * The review surface is core's allocation embed in its review-only view
 * (adapter 3.31 `view="plan-review"`), which every Replenishment page hosts
 * when its URL carries `?plan=` (containers/ReplenishmentView/PlanReviewPanel).
 * So a plan is reviewed on the planning page it concerns — the class's page
 * when the plan names one class, the page the reviewer is already on when it
 * is a Replenishment page, else /replenishment — never on /allocation-engine,
 * which is direct-nav only (Jeff, 2026-10-16).
 */
import { REPLENISHMENT_PAGES } from "./containers/ReplenishmentView/pages";
import { DRINK_MIX_CLASS, SPARKLING_CLASS } from "./containers/ReplenishmentView/constants";

/** The shape the kit hands across (ui-kit PlanReviewSeed); `actions` are the
 *  envelope's proposed actions, read as data. */
export interface PlanReviewSeedLike {
  title?: string | null;
  actions?: unknown[];
  prompt?: string | null;
  message?: string | null;
}

const CLASS_PAGE: Record<string, string> = Object.fromEntries(
  REPLENISHMENT_PAGES.flatMap((p) => (p.lockedClass ? [[p.lockedClass, p.basePath]] : [])),
);
const DEFAULT_PAGE = "/replenishment";

const mentions = (text: string, cls: string): boolean => new RegExp(`\\b${cls}\\b`, "i").test(text);

/** The classes the plan names, from everything the seed carries (titles,
 *  summaries, selectors, diffs): Sparkling / Drink Mix as the data spells them. */
export function classesNamed(seed: PlanReviewSeedLike | undefined): string[] {
  if (!seed) return [];
  let text = "";
  try {
    text = JSON.stringify([seed.title ?? "", seed.prompt ?? "", seed.actions ?? []]);
  } catch {
    return [];
  }
  return [SPARKLING_CLASS, DRINK_MIX_CLASS].filter((cls) => mentions(text, cls));
}

/** The Replenishment page a path is on (its basePath), or null. */
export const replenishmentPageAt = (pathname: string): string | null =>
  REPLENISHMENT_PAGES.map((p) => p.basePath)
    .sort((a, b) => b.length - a.length)
    .find((base) => pathname === base || pathname.startsWith(`${base}/`)) ?? null;

/** The page to review a plan on. ONE class named → that class's page; otherwise
 *  the Replenishment page the reviewer is on, else /replenishment. */
export function planReviewPathFor(seed?: unknown, pathname = window.location.pathname): string {
  // The shell types the seed as unknown; it is the kit's PlanReviewSeed, read as data.
  const classes = classesNamed(seed && typeof seed === "object" ? (seed as PlanReviewSeedLike) : undefined);
  if (classes.length === 1 && CLASS_PAGE[classes[0]]) return CLASS_PAGE[classes[0]];
  return replenishmentPageAt(pathname) ?? DEFAULT_PAGE;
}
